<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useElementSize } from '@vueuse/core';
import { UiButton, UiDialog, UiIconButton, UiText, useApi, useToaster, setHelpScoutBeaconHidden, type ExpertReviewSchema } from '@shopexperts/shared';
import type { z } from 'zod';
import { useReviewShareBackgrounds } from '@/app/composables/reviews/useReviewShareBackgrounds';
import { shouldHideHelpScoutBeacon } from '@/app/utils/helpScoutBeaconVisibility';
import ReviewShareArtwork from './ReviewShareArtwork.vue';
import defaultBackground from '@/assets/images/review-share-background.png';

const props = defineProps<{ review: z.infer<typeof ExpertReviewSchema> }>();
const open = defineModel<boolean>('open', { default: false });
const toaster = useToaster();
const api = useApi();
const sharing = ref(false);
const { backgrounds, limit, uploadCount, isPending, isError, refetch, upload, remove } = useReviewShareBackgrounds();
const selectedKey = ref<string | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const artwork = ref<HTMLElement | null>(null);
const artworkComponent = ref<InstanceType<typeof ReviewShareArtwork> | null>(null);
const previewContainer = ref<HTMLElement | null>(null);
const { width } = useElementSize(previewContainer);
const exporting = ref(false);
const preparingUpload = ref(false);
const imageReady = ref(false);
const imageError = ref(false);
const pendingDelete = ref<string | number | null>(null);
const selected = computed(() => backgrounds.value.find(item => item.key === selectedKey.value));
const busy = computed(() => preparingUpload.value || upload.isPending.value || remove.isPending.value || exporting.value || sharing.value);
const canUpload = computed(() => !isPending.value && !isError.value && uploadCount.value < limit.value && !busy.value);
const reviewer = computed(() => props.review.is_anonymous ? 'Anonymous client' : props.review.client_name);
const socialLinks = computed(() => {
    const url = '{url}';
    const text = encodeURIComponent(`“${props.review.review_text}” — ${reviewer.value}`);
    return [
        { label: 'LinkedIn', icon: 'linkedin' as const, href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}` },
        { label: 'X (Twitter)', icon: 'x-mono' as const, href: `https://twitter.com/intent/tweet?text=${text}&url=${url}` },
        { label: 'Facebook', icon: 'facebook' as const, href: `https://www.facebook.com/sharer/sharer.php?u=${url}` },
    ];
});

async function shareReview(href: string) {
    if (busy.value) return;
    // Open within the user gesture; navigating after the API response alone
    // would be blocked by popup blockers. Close it if preparation fails.
    const composer = window.open('about:blank', '_blank');
    if (composer) { composer.opener = null; composer.document.title = 'Preparing your review'; composer.document.body.textContent = 'Preparing your review image…'; }
    sharing.value = true;
    try {
        const result = await api.expert.reviewShareBackgrounds.generateShare({
            params: { id: String(props.review.id) },
            body: { background_key: selectedKey.value },
        });
        const url = href.replace('{url}', encodeURIComponent(result.share_url));
        if (composer) composer.location.replace(url);
        else window.location.assign(url);
    } catch (error) {
        composer?.close();
        showError('Could not prepare review sharing', error);
    } finally { sharing.value = false; }
}

const backgroundUrl = computed(() => selected.value?.url || defaultBackground);

watch(backgroundUrl, () => {
    imageReady.value = false;
    imageError.value = false;
});

onMounted(() => setHelpScoutBeaconHidden(true));
onUnmounted(() => {
    if (!shouldHideHelpScoutBeacon(window.location.pathname)) setHelpScoutBeaconHidden(false);
});

function showError(title: string, error: unknown) {
    const apiError = error as { response?: { data?: { message?: string } }; message?: string };
    toaster.create({ title, description: apiError?.response?.data?.message || apiError?.message || 'Please try again.', type: 'error' });
}

async function uploadBackground(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !canUpload.value) return;
    preparingUpload.value = true;
    try {
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            throw new Error('Choose a JPG, PNG, or WebP image.');
        }
        if (file.size > 10 * 1024 * 1024) throw new Error('Choose an image smaller than 10 MiB.');
        const bitmap = await createImageBitmap(file);
        const tooLarge = bitmap.width > 4000 || bitmap.height > 4000;
        bitmap.close();
        if (tooLarge) throw new Error('Images must be no larger than 4000 × 4000 pixels.');
        const background = await upload.mutateAsync(file);
        selectedKey.value = background.key;
    } catch (error) {
        showError('Could not upload background', error);
    } finally { preparingUpload.value = false; }
}

async function deleteBackground() {
    const id = pendingDelete.value;
    if (id === null) return;
    try {
        const deletedKey = backgrounds.value.find(item => item.id === id)?.key;
        await remove.mutateAsync(id);
        if (selectedKey.value === deletedKey) selectedKey.value = null;
        pendingDelete.value = null;
    } catch (error) {
        showError('Could not delete background', error);
    }
}

async function downloadImage() {
    if (!artwork.value || busy.value || !imageReady.value || imageError.value) return;
    exporting.value = true;
    const target = artwork.value.querySelector<HTMLElement>('.review-share-artwork');
    if (!target) { exporting.value = false; return; }
    try {
        await artworkComponent.value?.fitQuote();
        await nextTick();
        const { toPng } = await import('html-to-image');
        const dataUrl = await toPng(target, {
            // Copy the exact font shorthand; html-to-image rounds font-size down.
            includeStyleProperties: ['font', ...Array.from(getComputedStyle(target)).filter(name => name !== 'font-size' && name !== 'font')],
            pixelRatio: 3.2,
            width: 600,
            height: 337.5,
            // Refresh images cached before the CDN started returning CORS headers.
            fetchRequestInit: { cache: 'reload' },
            style: { transform: 'none' },
        });
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `shopexperts-review-${props.review.id}.png`;
        link.click();
    } catch (error) {
        showError('Could not download image', error);
    } finally {
        exporting.value = false;
    }
}
</script>

<template>
    <UiDialog v-model="open" size="xl" max-width="648px" appearance="compact" title="Share review" text="Let your network know about the work you’ve done.">
        <div class="review-share-body mx-auto flex max-w-[600px] flex-col gap-6">
            <div ref="previewContainer" class="relative overflow-hidden rounded-[14.55px] after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:border after:border-se-gray-300"
                :style="{ height: `${(width || 600) * 9 / 16}px` }">
                <div ref="artwork" :style="{ transform: `scale(${(width || 600) / 600})`, transformOrigin: 'top left', width: '600px', height: '337.5px' }">
                    <ReviewShareArtwork ref="artworkComponent" :review="review" :background="backgroundUrl"
                        @loaded="imageReady = true" @error="imageError = true; imageReady = false" />
                </div>
            </div>
            <UiText as="p" v-if="imageError" role="alert" variant="p-small" class="text-red-600" text="This background could not load. Select another background to download your image." />

            <div class="flex flex-col gap-2">
                <UiText as="p" variant="p-small" text="Select background:" />
                <UiText as="p" v-if="isPending" role="status" variant="small" text="Loading backgrounds…" />
                <div v-else-if="isError" class="flex items-center gap-3" role="alert">
                    <UiText as="p" variant="small" text="Could not load backgrounds." />
                    <UiButton variant="ghost" size="sm" label="Try again" @click="refetch()" />
                </div>
                <div class="flex flex-wrap gap-2" role="group" aria-label="Review backgrounds">
                    <UiButton variant="outline" class="review-background-option h-14 w-14 shrink-0 overflow-hidden !rounded !p-0" aria-label="Default background"
                        :aria-pressed="!selected"
                        :disabled="busy" @click="selectedKey = null">
                        <template #before_label><img :src="defaultBackground" alt="" class="h-14 w-14 max-w-none object-cover" /></template>
                    </UiButton>
                    <div v-for="(background, index) in backgrounds" :key="background.key" class="relative">
                        <UiButton variant="outline" class="review-background-option h-14 w-14 overflow-hidden !rounded !p-0" :disabled="busy"
                            :aria-label="`${background.is_preset ? 'Preset' : 'Custom'} background ${index + 1}`"
                            :aria-pressed="selectedKey === background.key"
                            @click="selectedKey = background.key">
                            <template #before_label><img :src="background.url" alt="" class="h-14 w-14 max-w-none object-cover" /></template>
                        </UiButton>
                        <UiIconButton v-if="!background.is_preset && background.id !== null" icon="close" variant="outline"
                            :aria-label="`Delete custom background ${index + 1}`" class="absolute -right-1 -top-1 !h-5 !w-5 bg-white !p-0"
                            :disabled="busy" @click="pendingDelete = background.id" />
                    </div>
                    <UiIconButton icon="upload" variant="outline" aria-label="Upload background" class="!h-14 !w-14 !rounded"
                        :disabled="!canUpload" @click="fileInput?.click()" />
                    <input ref="fileInput" type="file" accept="image/jpeg,image/png,image/webp" class="hidden" aria-label="Background image file" @change="uploadBackground" />
                </div>
                <UiText as="p" variant="caption-normal" class="review-share-hint text-se-gray-700" :text="'If you upload your own background image we recommend an aspect ratio of 16:9.\nSupported file formats: JPG, PNG, WEBP. Up to 10 MiB and 4000 × 4000 pixels.'" />
                <UiText as="p" v-if="!isPending && !isError && (upload.isPending.value || uploadCount >= limit)" variant="small" class="text-se-gray-700" role="status"
                    :text="upload.isPending.value ? 'Uploading background…' : limit === 0 ? 'Custom background uploads are currently disabled.' : `${uploadCount} of ${limit} custom backgrounds used${uploadCount >= limit ? '. Delete a custom background to upload another.' : '.'}`" />
            </div>

            <div class="flex flex-col gap-2">
                <UiText as="p" variant="p" text="Share your review via" />
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <UiButton v-for="social in socialLinks" :key="social.label" variant="outline" :label="social.label" :icon="social.icon"
                        :disabled="busy" @click="shareReview(social.href)" />
                </div>
            </div>
        </div>
        <template #footer>
            <div class="review-share-footer mx-auto flex w-full max-w-[600px] flex-wrap justify-between gap-3">
                <UiButton label="Cancel" variant="outline" @click="open = false" />
                <UiButton :label="exporting ? 'Preparing image…' : 'Download Image'" icon="download-01"
                    :disabled="busy || !imageReady || imageError" @click="downloadImage" />
            </div>
        </template>
    </UiDialog>
    <UiDialog :model-value="pendingDelete !== null" title="Delete background?" text="This image will be permanently removed from your saved backgrounds."
        primary-action-label="Delete background" primary-action-variant="danger" secondary-action-label="Cancel"
        :loading="remove.isPending.value" @update:model-value="value => { if (!value) pendingDelete = null }"
        @primary-action="deleteBackground" @secondary-action="pendingDelete = null" />
</template>

<style scoped>
:deep(.review-share-hint) { font-size: 12px; line-height: 18px; }
:deep(button[aria-label="Upload background"] svg) { width: 24px; height: 24px; }
:deep(.review-background-option) { position: relative; }
:deep(.review-background-option[aria-pressed="true"])::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 2px solid #1f2125;
    border-radius: inherit;
    box-shadow: inset 0 0 0 2px white;
    pointer-events: none;
}
:deep(.review-background-option:focus-visible) { outline: 2px solid #1f2125; outline-offset: -2px; }
.review-share-footer :deep(button) { min-height: 40px; height: 40px; border-radius: 8px; gap: 8px; }
.review-share-footer :deep(svg) { width: 20px; height: 20px; }
.review-share-footer :deep(.se-font-button) { font-size: 16px; line-height: 24px; }
</style>
